import React, { useState } from 'react';
import styled from 'styled-components';
import InfiniteScroll from 'react-infinite-scroller';

import theme from '../../../constants/themes';
import { Category, Subcategory } from '../../../domain/entities/category';
import { Item } from '../../../domain/entities/item';

import { Header } from '../../organisms/Header';
import { Tabs } from '../../molecules/Tabs';
import { Tab } from '../../atoms/Tab';
import { Footer } from '../../organisms/Footer';
import { getLabel } from '../../../util/strings';

const MainContainer = styled.div`
  background-color: ${theme.colors.drawerBg};
`;

const HeaderWrapper = styled.div`
  background-color: ${theme.colors.tabBgMugi};
  padding-top: 60px;
`;

const TabsMobile = styled.div`
  @media ${theme.breakpoints.mobile} {
    display: block;
  }

  @media ${theme.breakpoints.tablet} {
    display: none;
  }

  @media ${theme.breakpoints.pc} {
    display: none;
  }
`;

const TabsPC = styled.div`
  @media ${theme.breakpoints.mobile} {
    display: none;
  }

  @media ${theme.breakpoints.pc} {
    display: block;
  }
`;

const EmptyContainer = styled.div`
  min-height: 50vh;
  display: flex;
  justify-content: center;
  align-items: center;
  
  font-family: "${theme.fonts.primary}";
  color: ${theme.colors.white};

  @media ${theme.breakpoints.mobile} {
    font-size: 18px;
    line-height: 34px;
  }

  @media ${theme.breakpoints.pc} {
    font-size: 24px;
    line-height: 36px;
  }
`;

const ContentContainer = styled.div`
  margin: 32px 24px 73px;

  @media ${theme.breakpoints.pc} {
    margin: 56px 151px calc(77px - 10em) 151px;
  }

  @media ${theme.breakpoints.wideScreen} {
    margin: 79px 137px 101px 151px;
    min-height: 50vh;
  }
`;

const SubCategoryTitle = styled.div`
  font-family: "${theme.fonts.primary}";
  font-size: 32px;
  font-weight: 600;
  color: ${theme.colors.white};

  @media ${theme.breakpoints.mobile} {
    &:not(:first-of-type) {
      margin-top: 58px;
    }
  }

  @media ${theme.breakpoints.pc} {
    font-size: 40px;
    margin-left: 16px;
  }
`;

const MenuItemsContainer = styled(InfiniteScroll)`
  @media ${theme.breakpoints.pc} {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const MenuItem = styled.div`
  min-height: 96px;

  @media ${theme.breakpoints.mobile} {
    &:not(:first-of-type) {
      margin-top: 46px;
    }
  }

  @media ${theme.breakpoints.pc} {
    width: calc(25% - 32px);
    margin: 8px 16px 32px;
  }
`;

const ItemDetailsRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const ItemImageContainer = styled.div`
  width: 80px;
`;

const ItemImageWrapper = styled.img`
  width: 100%;
`;

const ItemDetailsContainer = styled.div`
  width: 100%;
`;

const ItemNameWrapper = styled.div<{ isForBeer?: boolean }>`
  font-family: "${theme.fonts.primary}";
  font-size: 24px;
  font-weight: 600;
  color: ${theme.colors.tabBgMugi};
  margin-top: ${({ isForBeer }) => (isForBeer ? '8px' : 0)}
`;

const ItemDetails1Wrapper = styled.div`
  font-family: "${theme.fonts.primary}";
  font-size: 16px;
  font-weight: 300;
  color: ${theme.colors.white};
  margin-top: 8px;
  white-space: pre-line;
  line-height: 2.5;
  display: flex;
  flex-direction: column;

  @media ${theme.breakpoints.pc} {
    font-size: 18px;
  }
`;

const ItemDetails2Wrapper = styled.div`
  width: 100%;
  font-family: "${theme.fonts.primary}";
  font-size: 16px;
  font-weight: 300;
  color: ${theme.colors.white};
  white-space: pre-line;
  word-wrap: break-word;
  line-height: 2.5;

  @media ${theme.breakpoints.pc} {
    font-size: 18px;
  }
`;

const ItemDetails3Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const ItemDetails3Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1em;
  font-family: "${theme.fonts.primary}";
  font-size: 16px;
  font-weight: 300;
  color: ${theme.colors.white};
  white-space: pre-line;
  line-height: 2.5;

  @media ${theme.breakpoints.pc} {
    font-size: 18px;
  }
`;

const BreweryContainer = styled.div`
  display: flex;
`;

const BreweryWrapper = styled.div`
  background: ${theme.colors.tabBgMugi2};
  font-size: 16px;
  font-weight: 300;
  font-family: "${theme.fonts.primary}";
  color: ${theme.colors.white};
  padding: 0.3em .5em;
`;

const SubHeading = styled.p`
  font-size: 24px;
  font-weight: 600;
  line-height: 36px;
  color: ${theme.colors.white};
  font-family: "${theme.fonts.primary}";
`;

const RenderItems = ({
  currentLang,
  categoryLabel,
  categoryEngLabel,
  onLoadMoreItems,
  items,
  isForMonitor,
  isForBeer,
}: {
  currentLang: string;
  categoryLabel: string;
  categoryEngLabel?: string;
  onLoadMoreItems: () => void;
  items: Item[];
  isForMonitor: boolean;
  isForBeer: boolean;
}) => {
  return (
    <>
      <SubCategoryTitle>
        {getLabel(currentLang, categoryLabel, categoryEngLabel)}
      </SubCategoryTitle>
      <MenuItemsContainer
        pageStart={1}
        loadMore={onLoadMoreItems}
        hasMore={false}>
        {items?.map((value, index) => (
          <MenuItem key={value.id}>
            {isForMonitor && <SubHeading>Tap {index + 1}</SubHeading>}
            <ItemDetailsRow>
              <ItemDetailsContainer>
                {(value.breweryName || value.breweryNameEn) && (
                  <BreweryContainer>
                    <BreweryWrapper>
                      {getLabel(
                        currentLang,
                        value.breweryName,
                        value.breweryNameEn,
                      )}
                    </BreweryWrapper>
                  </BreweryContainer>
                )}
                <ItemNameWrapper isForBeer={isForBeer}>
                  {getLabel(currentLang, value.name, value.nameEn)}
                </ItemNameWrapper>
                <ItemDetails1Wrapper>
                  {isForBeer && (
                    <div>
                      {value.abv && value.ibu
                        ? `ABV: ${value.abv} | IBU: ${value.ibu}\n`
                        : value.abv
                        ? `ABV: ${value.abv}\n`
                        : value.ibu
                        ? `IBU: ${value.ibu}\n`
                        : ''}
                    </div>
                  )}
                  {isForBeer &&
                    (value.beerType || value.beerTypeEn ? (
                      <div>
                        {getLabel(
                          currentLang,
                          value.beerType,
                          value.beerTypeEn,
                        )}
                      </div>
                    ) : null)}
                  {isForBeer &&
                    (value.madeIn || value.madeInEn ? (
                      <div>
                        {getLabel(currentLang, value.madeIn, value.madeInEn)}
                      </div>
                    ) : null)}
                  {!isForBeer &&
                    (value.variations.length > 0
                      ? value.variations.length > 1
                        ? value.variations.map(({ name, nameEn, price }) => (
                            <>{`${getLabel(
                              currentLang,
                              name,
                              nameEn,
                            )}: ¥${price.toLocaleString()}\n`}</>
                          ))
                        : `¥${value.variations[0].price.toLocaleString()}\n`
                      : null)}
                </ItemDetails1Wrapper>
              </ItemDetailsContainer>
              {value.imgUrl && (
                <ItemImageContainer>
                  <ItemImageWrapper src={value.imgUrl} />
                </ItemImageContainer>
              )}
            </ItemDetailsRow>
            <ItemDetailsRow>
              <ItemDetails2Wrapper>
                {(value.description || value.descriptionEn) &&
                  getLabel(currentLang, value.description, value.descriptionEn)}
              </ItemDetails2Wrapper>
            </ItemDetailsRow>
            {isForBeer && (
              <ItemDetails3Row>
                <ItemDetails3Wrapper>
                  {value.variations.length > 0 &&
                    value.variations.map(({ name, nameEn, price }, index) => (
                      <div key={`price-${index}`}>{`${getLabel(
                        currentLang,
                        name,
                        nameEn,
                      )}: ¥${price.toLocaleString()}`}</div>
                    ))}
                </ItemDetails3Wrapper>
              </ItemDetails3Row>
            )}
          </MenuItem>
        ))}
      </MenuItemsContainer>
    </>
  );
};

type Props = {
  categories: Category[];
  items: Item[];
  selectedCategory?: Category;
  selectedSubcategory?: Subcategory;
  isForBeer?: boolean;
  onChangeCategory: (value?: Category) => void;
  onChangeSubcategory: (value?: Subcategory) => void;
  onLoadMoreItems: () => void;
};

const MenuCategoryMugi = ({
  categories,
  items,
  selectedCategory,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectedSubcategory,
  onChangeCategory,
  onChangeSubcategory,
  onLoadMoreItems,
  isForBeer = false,
}: Props): React.ReactElement => {
  const categoryList = categories.map(
    ({ id, name, nameEn, subcategories, is_for_monitor }) => ({
      value: id,
      label: name,
      engLabel: nameEn,
      is_for_monitor: is_for_monitor,
      subItems: subcategories.map(({ id, name, nameEn }) => ({
        value: id,
        label: name,
        engLabel: nameEn,
      })),
    }),
  );

  const onSelectCategory = (tabValue: string) => () => {
    const newSelCateg = categories.find(e => e.id === tabValue);
    onChangeCategory(newSelCateg);
    if (newSelCateg?.subcategories.length === 0) onChangeSubcategory(undefined);
  };

  const [currentLang, setCurrentLang] = useState(
    window.sessionStorage.getItem('defaultLanguage') ?? 'JP',
  );

  const handleLanguageSwitch = (value: string) => {
    window.sessionStorage.setItem('defaultLanguage', value);
    setCurrentLang(value);
  };

  return (
    <MainContainer>
      <HeaderWrapper>
        <Header
          translationToggle
          isMugi
          defaultLanguage={currentLang}
          onSwitchLanguage={handleLanguageSwitch}
        />
        <TabsMobile>
          <Tabs colorScheme="light" selectedValue={selectedCategory?.id || ''}>
            {categoryList.map(({ value, label, engLabel }, index) => (
              <Tab
                key={index}
                value={value}
                label={getLabel(currentLang, label, engLabel)}
                colorScheme="light"
                isActive={selectedCategory?.id === value}
                onClick={onSelectCategory(value)}
              />
            ))}
          </Tabs>
        </TabsMobile>
        <TabsPC>
          <Tabs colorScheme="light" selectedValue={selectedCategory?.id || ''}>
            {categoryList.map(({ value, label, engLabel }, index) => (
              <Tab
                key={index}
                value={value}
                label={getLabel(currentLang, label, engLabel)}
                colorScheme="light"
                isActive={selectedCategory?.id === value}
                onClick={onSelectCategory(value)}
              />
            ))}
          </Tabs>
        </TabsPC>
      </HeaderWrapper>
      {categoryList.length <= 0 && items.length <= 0 ? (
        <EmptyContainer>商品が登録されていません。</EmptyContainer>
      ) : (
        <ContentContainer>
          {categoryList.map((cat, index) => {
            if (selectedCategory && selectedCategory.id === cat.value) {
              if (cat.subItems && cat.subItems.length > 0) {
                const itemsInCategoryOnly = items.filter(e => !e.subcategoryId);

                return (
                  <>
                    {itemsInCategoryOnly.length > 0 && (
                      <RenderItems
                        currentLang={currentLang}
                        categoryLabel={cat.label}
                        categoryEngLabel={cat.engLabel}
                        onLoadMoreItems={onLoadMoreItems}
                        items={itemsInCategoryOnly}
                        isForMonitor={selectedCategory?.is_for_monitor}
                        isForBeer={isForBeer}
                      />
                    )}
                    {cat.subItems.map(subItem => {
                      const subcatItems = items.filter(
                        item => item.subcategoryId === subItem.value,
                      );

                      return (
                        <RenderItems
                          key={subItem.value}
                          currentLang={currentLang}
                          categoryLabel={subItem.label}
                          categoryEngLabel={subItem.engLabel}
                          onLoadMoreItems={onLoadMoreItems}
                          items={subcatItems}
                          isForMonitor={selectedCategory?.is_for_monitor}
                          isForBeer={isForBeer}
                        />
                      );
                    })}
                  </>
                );
              } else {
                return (
                  <RenderItems
                    key={index}
                    currentLang={currentLang}
                    categoryLabel={cat.label}
                    categoryEngLabel={cat.engLabel}
                    onLoadMoreItems={onLoadMoreItems}
                    items={items}
                    isForMonitor={selectedCategory?.is_for_monitor}
                    isForBeer={isForBeer}
                  />
                );
              }
            }
          })}
        </ContentContainer>
      )}
      <Footer colorScheme="dark" />
    </MainContainer>
  );
};

export default MenuCategoryMugi;
